'use client';

import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import {
    Icon,
    IconName,
    IconSize,
    Spacer,
    SpacerSize,
    Title,
    TitleLevels,
    TitleMarkup,
    View,
} from '@bytel/trilogy-react-ts';

import { Connectivity, PlanType } from '@app-types/product';

import { useHome } from '@landing/hooks';

import { BannerText } from './banner-text';
import { CtaAddToCart } from './cta-add-to-cart';
import { DataList } from './data-list';
import { NetworkButtons } from './network-buttons';
import { BAndyouPrice } from './plan-header/b-and-you-price';
import { ProductDetail } from './product-detail';

function getHighlightedPlan(plans: PlanType[]): PlanType | undefined {
    return plans.find((plan) => plan.isHighlighted);
}

interface BAndYouConfiguratorProps {
    plans: PlanType[];
    isMobileFirst: boolean;
}

export function BAndyouConfigurator({ plans, isMobileFirst }: Readonly<BAndYouConfiguratorProps>) {
    const {
        selectedPlan: _selectedPlan,
        setSelectedPlan,
        temporarilySelectedPlan: _temporarilySelectedPlan,
        setTemporarilySelectedPlan,
        selectedOption,
    } = useHome();
    const searchParams = useSearchParams();

    const shouldShowCta = !isMobileFirst;

    const defaultPlan = getHighlightedPlan(plans) ?? plans[0]!;

    const selectedPlan = isMobileFirst ? (_selectedPlan ?? defaultPlan) : _selectedPlan;
    const temporarilySelectedPlan = _temporarilySelectedPlan ?? defaultPlan;

    const baseProduct = selectedPlan ?? temporarilySelectedPlan;

    const is5gOffer = !!(selectedPlan?.connectivity === Connectivity.FIVE_G || selectedOption?.gencode);

    useEffect(() => {
        if (!plans[0]) {
            return;
        }

        const urlKeyParam = searchParams.get('url_key');
        const planFromUrl = plans.find((p) => p.url === urlKeyParam);

        if (planFromUrl) {
            setSelectedPlan(planFromUrl);
            setTemporarilySelectedPlan(planFromUrl);
        } else if (isMobileFirst) {
            setSelectedPlan(defaultPlan);
            setTemporarilySelectedPlan(null);
        } else {
            setSelectedPlan(null);
            setTemporarilySelectedPlan(defaultPlan);
        }
    }, [isMobileFirst]);

    if (!plans || plans.length === 0) {
        return;
    }

    return (
        <div data-testid='byou-container' className='pricing-table is-clipped'>
            <div className='pricing-plan'>
                <div className='pricing-plan-product has-hat'>
                    <BannerText
                        bannerText={isMobileFirst ? selectedPlan?.bannerText : _temporarilySelectedPlan?.bannerText}
                    />
                    <BAndyouPrice
                        isMobileFirst={isMobileFirst}
                        icon={
                            <>
                                {is5gOffer ? (
                                    <Icon className='is-blue-byou-icon' name={IconName.UI_5G} size={IconSize.MEDIUM} />
                                ) : null}
                                <div className='is-flex gap-small'>
                                    {(selectedPlan?.planPicto?.length ?? 0) > 0
                                        ? selectedPlan?.planPicto?.map((picto) => (
                                              <View
                                                  key={picto.public_url}
                                                  style={{ position: 'relative', width: '32px', height: '32px' }}
                                              >
                                                  <Image src={picto.public_url} alt={picto.title} fill />
                                              </View>
                                          ))
                                        : null}
                                </div>
                            </>
                        }
                        firstPlan={baseProduct}
                    />
                    <div className='plan-items'>
                        <div className='plan-item custom-padding is-narrow'>
                            <Title level={TitleLevels.THREE} markup={TitleMarkup.H2}>
                                Quel forfait vous faut-il&nbsp;?
                            </Title>
                            <DataList plans={plans} isMobileFirst={isMobileFirst} />
                            <Spacer size={SpacerSize.LARGE} />
                            <div>
                                <Title level={TitleLevels.THREE} markup={TitleMarkup.H2}>
                                    Quelle vitesse de connexion&nbsp;?
                                </Title>
                                <NetworkButtons baseProduct={baseProduct ?? null} />
                                <Spacer size={SpacerSize.MEDIUM} />
                                <ProductDetail product={baseProduct ?? null} />
                            </div>
                            {shouldShowCta ? (
                                <CtaAddToCart
                                    selectedProduct={selectedPlan}
                                    temporarilySelectedProduct={temporarilySelectedPlan}
                                    handleAddToCart={() => {
                                        if (temporarilySelectedPlan?.gencode !== selectedPlan?.gencode) {
                                            setSelectedPlan(temporarilySelectedPlan ?? null);
                                        } else {
                                            setSelectedPlan(null);
                                        }
                                    }}
                                    ctaLabel={{
                                        productAdded: 'Retirer ce forfait',
                                        productNotAdded: 'Ajouter ce forfait',
                                        tagAdded: 'Forfait ajouté',
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
